import { API_URL } from '~/constants';
import type { UserModule } from '~/types';
import type { RouteNamedMap } from 'vue-router/auto-routes';
import type { AxiosError } from 'axios';
import * as sdk from '@cirqll/sdk';
import { Capacitor } from '@capacitor/core';
import { ALWAYS_ALLOWED_ROUTES } from './auth';
import { redirectToLogin } from '~/utils/auth';
import { nativeLogout, nativeRefreshStrategy, nativeSessionEnded } from './native_auth';
import { i18n } from './i18n';

export const install: UserModule = () => {
    sdk.api.defaults.baseURL = API_URL;

    sdk.api.interceptors.request.use((config) => {
        config.headers['Accept-Language'] = i18n.global.locale.value;
        return config;
    });

    sdk.configureAuthRefresh({
        strategy: Capacitor.isNativePlatform() ? nativeRefreshStrategy() : sdk.cookieRefresh(),
        onLoggedOut: () => {
            const currentPath = window.location.pathname as keyof RouteNamedMap;

            if (ALWAYS_ALLOWED_ROUTES.includes(currentPath)) {
                return;
            }

            const hadAuthenticatedBefore = sdk.hasEverAuthenticated();
            localStorage.clear();

            if (Capacitor.isNativePlatform()) {
                void nativeSessionEnded();
            } else {
                void sdk.redirectToLogin({ promptLogin: hadAuthenticatedBefore });
            }
        },
        // The default sign-out navigates to auth.cirqll.nl/logout, which the WebView hands to Safari/Chrome.
        onSubscriptionExpired: Capacitor.isNativePlatform() ? () => void nativeLogout() : undefined,
    });

    let redirectingToTwoFactorSetup = false;

    sdk.api.interceptors.response.use(undefined, (error: AxiosError<any>) => {
        // The company enforces 2FA and this session hasn't set it up: the login sends the
        // user through the 2FA setup on the auth domain before returning to the app.
        if (error.response?.status === 403 && error.response.data?.requires_2fa_setup === true && !redirectingToTwoFactorSetup) {
            redirectingToTwoFactorSetup = true;
            void redirectToLogin();
        }

        throw error;
    });
};
